/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import IPDetails from "@/helper/IPDetails";
import i18n from "i18next";
import { safeGetLang, safeSetLang } from "@/helper/safeLocalStorage";
import { LANGUAGES } from "./constants";
// DD-2: Import the startup promise so fetchLanguage awaits it before checking
// localStorage — prevents duplicate IPDetails() calls when main.tsx initLanguage
// is still in-flight when the Header first mounts.
import { languageInitPromise } from "@/main";
import {
  getLanguageFromQuery,
  normalizeLocalizedPath,
} from "@/utils/localizedPath";

/**
 * Hook to initialize language from URL path or IP geolocation.
 * Returns the resolved language and a setter.
 */
export function useLanguageInit(): [string, (lang: string) => void] {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    safeGetLang() ?? "en",
  );

  useEffect(() => {
    const setDefault = (): void => {
      setSelectedLanguage("en");
      safeSetLang("en");
      i18n.changeLanguage("en");
    };

    const setLang = (lang: string): void => {
      setSelectedLanguage(lang);
      safeSetLang(lang);
      // Item 13 (Session 361): only switch when the language actually changes.
      //
      // This hook runs on every navigation, so without the guard every page
      // change raised a language-changed event and re-rendered every
      // translated component on the page to arrive at the language it was
      // already showing. The guard is a performance fix and nothing more — it
      // is deliberately NOT the fix for raw key names, which come from a
      // cached translation file being paired with newer code (see i18n.ts).
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    };

    const handlePathBased = (): void => {
      // E-6: normalizeLocalizedPath extracts the language prefix from the URL.
      const { lang: urlLang } = normalizeLocalizedPath(
        window.location.pathname,
      );
      if (urlLang && LANGUAGES.some((l) => l.id === urlLang)) {
        setLang(urlLang);
        return;
      }
      // Item 5 (Session 361): an explicit ?lang= outranks a stored preference,
      // in the same order i18n.ts resolves the initial language. Without this
      // the first render honoured the link and this hook put the stored
      // preference back on the next one, so the page changed language under
      // the customer a moment after it opened.
      const stated = getLanguageFromQuery();
      if (stated && LANGUAGES.some((l) => l.id === stated)) {
        setLang(stated);
        return;
      }
      // D-1: No language prefix in URL (e.g. after pressing Back).
      // Honour stored preference before resetting to English —
      // setDefault() is only for genuine first-time visitors.
      const stored = safeGetLang();
      const storedValid = stored && LANGUAGES.some((l) => l.id === stored);
      if (storedValid) {
        setLang(stored!);
      } else {
        setDefault();
      }
    };

    const fetchLanguage = async (): Promise<void> => {
      // DD-2: Await the startup initLanguage() promise before reading localStorage.
      // If main.tsx is still resolving the IPDetails call, we'd see an empty lang
      // and fire a duplicate request. Awaiting ensures we only call IPDetails here
      // if the startup fetch genuinely found nothing.
      await languageInitPromise;
      if (!safeGetLang()) {
        try {
          const data = await IPDetails();
          // AF-4: Prefer data.languages (e.g. 'pt-BR,en') over data.country_code
          // ('BR'). country_code is a geographic code, not a language code — users
          // in Brazil, Austria, Mexico etc. received English when country_code 'BR'
          // didn't match any supported language id. data.languages is always the
          // accurate primary language from the /api/geo/ response.
          let detectedLang = "en";
          if (data?.languages) {
            const primary = data.languages.split(",")[0].trim(); // e.g. 'pt-BR'
            const lang = primary.split("-")[0].toLowerCase(); // e.g. 'pt'
            if (LANGUAGES.some((l) => l.id === lang)) detectedLang = lang;
          } else if (data?.country_code) {
            const cc = data.country_code.toLowerCase();
            if (LANGUAGES.some((l) => l.id === cc)) detectedLang = cc;
          }
          if (detectedLang !== "en") {
            setLang(detectedLang);
            navigate(`/${detectedLang}`, { replace: true });
          } else {
            setDefault();
          }
        } catch {
          setDefault();
        }
      } else {
        handlePathBased();
      }
    };

    fetchLanguage();
  }, [navigate, location.pathname]);

  return [selectedLanguage, setSelectedLanguage];
}
